import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import API from '../utils/apiconfig';
import { useAuth } from '../context/Authcontext';
import { useGlobalError } from '../context/GlobalErrorContext';
import { getRankDisplay, rankTheme } from '../utils/rankBadge';

const LEVEL_STYLES = [
  { level: 1, color: 'bg-amber-500',   barGradient: 'from-amber-500 to-orange-500',   text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200',   label: 'Direct Referral' },
  { level: 2, color: 'bg-orange-400',  barGradient: 'from-orange-400 to-amber-400',  text: 'text-orange-700',  bg: 'bg-orange-50',  border: 'border-orange-200',  label: '2nd Level'       },
  { level: 3, color: 'bg-yellow-500',  barGradient: 'from-yellow-500 to-amber-400',  text: 'text-yellow-700',  bg: 'bg-yellow-50',  border: 'border-yellow-200',  label: '3rd Level'       },
  { level: 4, color: 'bg-lime-500',    barGradient: 'from-lime-500 to-emerald-400',  text: 'text-lime-700',    bg: 'bg-lime-50',    border: 'border-lime-200',    label: '4th Level'       },
  { level: 5, color: 'bg-emerald-500', barGradient: 'from-emerald-500 to-teal-400',   text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: '5th Level'       },
  { level: 6, color: 'bg-teal-500',    barGradient: 'from-teal-500 to-cyan-400',    text: 'text-teal-700',    bg: 'bg-teal-50',    border: 'border-teal-200',    label: '6th Level'       },
  { level: 7, color: 'bg-blue-500',    barGradient: 'from-blue-500 to-indigo-500',  text: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200',    label: '7th Level'       },
];

const PAYOUT_PLANS = [
  {
    id: 'month1',
    title: '1st Month Income Payout',
    subtitle: 'One-time bonus when a new member joins and pays',
    icon: '⚡',
    pts: [200, 50, 10, 10, 10, 10, 10],
    perLabel: 'one-time',
    headerGradient: 'from-violet-600 via-indigo-600 to-violet-700',
    accent: 'text-violet-600',
    accentBg: 'bg-violet-50',
    accentBorder: 'border-violet-200',
    shadow: 'shadow-violet-200/50',
    chipBg: 'bg-violet-50',
    chipBorder: 'border-violet-200',
    chipText: 'text-violet-700',
  },
  {
    id: 'month2_9',
    title: 'Month 2–9 Income Payout',
    subtitle: 'Recurring income each month from month 2 through month 9',
    icon: '🔄',
    pts: [30, 10, 5, 3, 2, 1, 0.5],
    perLabel: 'per month',
    headerGradient: 'from-cyan-600 via-teal-600 to-cyan-700',
    accent: 'text-cyan-600',
    accentBg: 'bg-cyan-50',
    accentBorder: 'border-cyan-200',
    shadow: 'shadow-cyan-200/50',
    chipBg: 'bg-cyan-50',
    chipBorder: 'border-cyan-200',
    chipText: 'text-cyan-700',
  },
];

const formatPts = (pts) => (Number.isInteger(pts) ? String(pts) : pts.toFixed(1));

const LEVEL_CONFIG = LEVEL_STYLES.map((style, index) => ({
  ...style,
  pts: PAYOUT_PLANS[1].pts[index],
  month1Pts: PAYOUT_PLANS[0].pts[index],
  month2_9Pts: PAYOUT_PLANS[1].pts[index],
}));

const MEDALS = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣', '6️⃣', '7️⃣'];

const RANK_ICONS = { bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎', diamond: '✨' };

function Network() {
  const { user }  = useAuth();
  const { reportError } = useGlobalError();
  const navigate  = useNavigate();
  const [data,      setData]      = useState(null);
  const [tierData,  setTierData]  = useState(null);
  const [copied,    setCopied]    = useState(false);
  const [openLevel, setOpenLevel] = useState(null);
  const [claimStatus, setClaimStatus] = useState(null);
  const [claimCode, setClaimCode] = useState('');
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimMsg, setClaimMsg] = useState('');

  useEffect(() => {
    if (!user?.id) {
      navigate('/login');
      return;
    }
    api.get(API.GET_REFERRALS).then((r) => setData(r.data)).catch(reportError);
    api.get(API.LEVEL_TIERS).then((r) => setTierData(r.data)).catch(() => {});
    api.get(API.REFERRAL_CLAIM_STATUS)
      .then((r) => setClaimStatus(r.data))
      .catch(() => setClaimStatus(null));
  }, [user?.id, navigate, reportError]);

  const submitReferralClaim = async (e) => {
    e.preventDefault();
    setClaimBusy(true);
    setClaimMsg('');
    try {
      const res = await api.post(API.REFERRAL_CLAIM_SUBMIT, {
        referral_code: claimCode.trim(),
      });
      setClaimStatus(res.data);
      setClaimCode('');
      setClaimMsg(res.data?.message || 'Referral claim submitted for admin review.');
    } catch (err) {
      setClaimMsg(err.response?.data?.error || 'Unable to submit referral claim.');
    } finally {
      setClaimBusy(false);
    }
  };

  const referralLink = `${window.location.origin}/login?ref=${user?.referral_code}`;

  const copyLink = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(
      `Join SuccessFlow — ₹500/month membership. Earn by referring people!\nUse my code: ${user?.referral_code}\nSign up: ${referralLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  if (!data) return <div className="min-h-screen bg-slate-50" aria-hidden />;

  const levels = LEVEL_CONFIG.map((cfg) => {
    const api_lvl = data.levels?.find(l => l.level === cfg.level);
    return { ...cfg, ...(api_lvl || { count: 0, active_count: 0, members: [] }) };
  });

  const payoutTotals = PAYOUT_PLANS.map((plan) =>
    plan.pts.reduce((sum, pts) => sum + pts, 0)
  );

  const barWidth = (pts, maxPts) => `${Math.max(18, (pts / maxPts) * 100)}%`;

  const rankBadge = getRankDisplay(tierData?.current || user);
  const rankIcon = rankBadge.show ? rankTheme(rankBadge.themeKey, {}, RANK_ICONS).icon : null;
  const nextTier = tierData?.current?.next_tier;
  const idsToNext = tierData?.current?.ids_to_next_tier;
  const networkIds = tierData?.current?.network_id_count ?? data?.total_network ?? 0;
  const rankStatusNotice =
    tierData?.current?.rank_status_notice || user?.rank_status_notice || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50/60 to-cyan-50 py-8 sm:py-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-5 sm:space-y-6">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl border border-white/80 bg-white/80 px-5 py-5 shadow-[0_16px_40px_-20px_rgba(15,23,42,0.2)] backdrop-blur-sm">
          <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br from-indigo-200/40 to-cyan-200/30 blur-2xl" aria-hidden />
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-500">Referral Network</p>
          <h1 className="mt-1 text-2xl font-black text-slate-900 tracking-tight sm:text-3xl"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>My Network</h1>
          <p className="mt-1.5 text-sm text-slate-500 max-w-xl">Build a 7-level network and earn across two payout phases — a strong 1st month bonus, then recurring income through month 9.</p>
        </div>

        {/* Referral claim — only if no sponsor yet and within 14 days */}
        {claimStatus && !claimStatus.has_referred_by ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-5 shadow-[0_12px_36px_-18px_rgba(15,23,42,0.12)]">
            <p className="text-xs font-bold text-amber-800 uppercase tracking-widest mb-1">
              Referral Claim Request
            </p>
            <p className="text-sm text-slate-600 mb-3">
              Forgot to enter a referral code at signup? Submit a claim within{' '}
              {claimStatus.claim_window_days || 14} days of joining. Admin will review and map it.
            </p>
            {claimStatus.latest_claim ? (
              <div className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-700 mb-3">
                Latest claim: <strong>{claimStatus.latest_claim.referral_code_entered}</strong>
                {' · '}
                <span className="font-semibold">{claimStatus.latest_claim.status}</span>
                {claimStatus.latest_claim.admin_note
                  ? ` — ${claimStatus.latest_claim.admin_note}`
                  : ''}
              </div>
            ) : null}
            {claimStatus.can_submit ? (
              <form onSubmit={submitReferralClaim} className="flex flex-col sm:flex-row gap-2">
                <input
                  value={claimCode}
                  onChange={(e) => setClaimCode(e.target.value)}
                  placeholder="Enter referral code"
                  className="flex-1 rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm"
                  required
                />
                <button
                  type="submit"
                  disabled={claimBusy}
                  className="rounded-xl bg-amber-700 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
                >
                  {claimBusy ? 'Submitting…' : 'Submit claim'}
                </button>
              </form>
            ) : (
              <p className="text-sm text-amber-900 bg-white border border-amber-200 rounded-xl px-3 py-2">
                {claimStatus.blocked_reason
                  || (claimStatus.within_claim_window
                    ? 'A claim is already pending review.'
                    : 'Claim window has closed.')}
                {claimStatus.within_claim_window && claimStatus.days_remaining != null
                  ? ` (${claimStatus.days_remaining} day(s) left)`
                  : ''}
              </p>
            )}
            {claimMsg ? (
              <p className="mt-2 text-sm text-slate-700">{claimMsg}</p>
            ) : null}
          </div>
        ) : null}

        {/* Referral link card */}
        <div className="rounded-2xl border border-white/90 bg-white/90 p-5 shadow-[0_12px_36px_-18px_rgba(15,23,42,0.18)] backdrop-blur-sm">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Your Referral Link</p>
              <p className="text-sm text-slate-500">Share to grow your network and earn monthly commissions</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-slate-400 mb-0.5">Code</p>
              <p className="font-mono text-xl font-black text-slate-900 tracking-widest bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl">
                {user?.referral_code}
              </p>
            </div>
          </div>
          <div className="flex gap-2 mb-3">
            <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-400 font-mono overflow-hidden text-ellipsis whitespace-nowrap">
              {referralLink}
            </div>
            <button onClick={copyLink}
              className={`px-4 py-2 rounded-lg text-xs font-bold border transition-colors flex-shrink-0 ${
                copied ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}>
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-400">Share via:</span>
            <button onClick={shareWhatsApp}
              className="text-xs text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-3 py-1.5 transition-colors font-semibold">
              💬 WhatsApp
            </button>
            <button onClick={() => navigator.share?.({ title: 'Join SuccessFlow!', url: referralLink })}
              className="text-xs text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-3 py-1.5 transition-colors font-semibold">
              📤 Share
            </button>
          </div>
        </div>

        {/* Network stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { val: data.direct_count,  label: 'Direct Refs',   sub: 'Level 1 only'     },
            { val: data.total_network, label: 'Total Network', sub: 'All 7 levels'      },
            { val: data.total_active,  label: 'Active Members',sub: 'Paid this month'   },
            { val: `⭐ ${data.reward_points}`, label: 'Your Points', sub: '₹1 per point' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border border-white/90 bg-white/90 p-4 text-center shadow-sm backdrop-blur-sm">
              <p className="text-xl font-black text-slate-900"
                style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>{s.val}</p>
              <p className="text-xs font-semibold text-slate-600 mt-0.5">{s.label}</p>
              <p className="text-[10px] text-slate-400">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Rank ladder (network ID count) */}
        {tierData?.tiers?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Network rank</p>
                <p className="text-sm text-slate-500">
                  {tierData.qualification_note || 'Rank is based on total referral IDs in your downline.'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400 mb-0.5">Your network IDs</p>
                <p className="text-2xl font-black text-slate-900">{networkIds}</p>
                {rankBadge.show ? (
                  <p className="text-sm font-bold text-amber-600 mt-1">
                    {rankIcon} {rankBadge.label}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 mt-1">Badge unlocks at Bronze</p>
                )}
              </div>
            </div>

            {rankStatusNotice?.message && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-4 leading-relaxed">
                {rankStatusNotice.message}
              </p>
            )}

            {nextTier && idsToNext != null && idsToNext > 0 && (
              <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 mb-4">
                {idsToNext} more active ID{idsToNext !== 1 ? 's' : ''} to reach{' '}
                <strong>{nextTier.label || nextTier.slug}</strong>
              </p>
            )}

            <div className="space-y-2">
              {tierData.tiers.map((tier) => {
                const pct = tier.min_network_ids
                  ? Math.min(100, Math.round((networkIds / tier.min_network_ids) * 100))
                  : 0;
                return (
                  <div
                    key={tier.slug}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                      tier.is_current
                        ? 'border-amber-300 bg-amber-50'
                        : tier.qualified
                          ? 'border-emerald-200 bg-emerald-50/50'
                          : 'border-slate-100 bg-slate-50'
                    }`}
                  >
                    <div className="w-8 text-center text-lg flex-shrink-0">
                      {tier.qualified ? '✓' : tier.level_no}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{tier.label}</p>
                      <p className="text-[10px] text-slate-400">{tier.min_network_ids}+ network IDs</p>
                      <div className="h-1.5 bg-slate-200 rounded-full mt-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${tier.qualified ? 'bg-emerald-500' : 'bg-indigo-400'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    {tier.is_current && (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 7-Level breakdown */}
        <div className="overflow-hidden rounded-2xl border border-white/90 bg-white/90 shadow-[0_16px_44px_-20px_rgba(15,23,42,0.22)] backdrop-blur-sm">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-indigo-50/40 px-5 py-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Earnings Breakdown</p>
                <p className="mt-1 text-base font-black text-slate-900 sm:text-lg">7-Level Commission Structure</p>
                <p className="mt-1 text-xs text-slate-500 max-w-md">
                  Each level pays differently in month 1 vs months 2–9. Expand a row to view members.
                </p>
              </div>
              <div className="flex flex-wrap gap-2.5">
                <div className="rounded-2xl border border-violet-200/80 bg-gradient-to-br from-violet-50 to-white px-4 py-2.5 shadow-sm">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-violet-500">Month 1 max</p>
                  <p className="text-lg font-black text-violet-700 leading-tight">{formatPts(payoutTotals[0])} <span className="text-xs font-semibold">pts</span></p>
                </div>
                <div className="rounded-2xl border border-cyan-200/80 bg-gradient-to-br from-cyan-50 to-white px-4 py-2.5 shadow-sm">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-cyan-500">Months 2–9 max</p>
                  <p className="text-lg font-black text-cyan-700 leading-tight">{formatPts(payoutTotals[1])} <span className="text-xs font-semibold">pts/mo</span></p>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden sm:grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4 border-b border-slate-100 bg-slate-50/80 px-5 py-2.5 list-page-row-header">
            <span>Level</span>
            <span>Network</span>
            <span className="text-center text-violet-500">Month 1</span>
            <span className="text-center text-cyan-500">Mo 2–9</span>
            <span className="text-right">Status</span>
          </div>

          <div className="space-y-1.5 p-2 sm:p-3">
            {levels.map((lvl) => {
              const isOpen     = openLevel === lvl.level;
              const hasMembers = lvl.members?.length > 0;
              const activePct  = lvl.count > 0 ? Math.round((lvl.active_count / lvl.count) * 100) : 0;
              return (
                <div key={lvl.level} className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-shadow hover:shadow-md">
                  <button
                    type="button"
                    onClick={() => setOpenLevel(isOpen ? null : lvl.level)}
                    className={`w-full flex flex-wrap items-center gap-3 px-4 py-3.5 text-left transition-colors sm:gap-4 sm:px-5 ${isOpen ? 'bg-slate-50/80' : 'hover:bg-slate-50/60'}`}
                  >
                    <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${lvl.barGradient} text-sm font-black text-white shadow-md`}>
                      {lvl.level}
                    </div>
                    <div className="min-w-0 flex-1 basis-[140px]">
                      <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                        <span className="text-base">{MEDALS[lvl.level - 1]}</span>
                        {lvl.label}
                        {lvl.level === 1 && (
                          <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">Direct</span>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-400">
                        {lvl.count > 0 ? `${lvl.count} member${lvl.count !== 1 ? 's' : ''} · ${lvl.active_count} active` : 'No members yet'}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 gap-2">
                      <div className="min-w-[72px] rounded-xl border border-violet-200/80 bg-gradient-to-b from-violet-50 to-white px-3 py-2 text-center">
                        <p className="text-sm font-black text-violet-700">+{formatPts(lvl.month1Pts)}</p>
                        <p className="text-[9px] font-semibold uppercase tracking-wide text-violet-400">month 1</p>
                      </div>
                      <div className="min-w-[72px] rounded-xl border border-cyan-200/80 bg-gradient-to-b from-cyan-50 to-white px-3 py-2 text-center">
                        <p className="text-sm font-black text-cyan-700">+{formatPts(lvl.month2_9Pts)}</p>
                        <p className="text-[9px] font-semibold uppercase tracking-wide text-cyan-400">mo 2–9</p>
                      </div>
                    </div>
                    <div className="hidden w-24 flex-shrink-0 sm:block">
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-full rounded-full bg-gradient-to-r ${lvl.barGradient}`}
                          style={{ width: `${activePct}%` }} />
                      </div>
                      <p className="mt-1 text-right text-[9px] font-medium text-slate-400">
                        {lvl.count > 0 ? `${activePct}% active` : '—'}
                      </p>
                    </div>
                    <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border text-xs transition-transform ${isOpen ? 'rotate-180 border-indigo-200 bg-indigo-50 text-indigo-500' : 'border-slate-200 bg-white text-slate-400'}`}>
                      ▼
                    </span>
                  </button>

                  {isOpen && (
                    <div className={`px-5 pb-4 ${lvl.bg} border-t ${lvl.border}`}>
                      {!hasMembers ? (
                        <div className="flex items-center gap-3 py-4 text-slate-400">
                          <span className="text-2xl opacity-40">👥</span>
                          <div>
                            <p className="text-sm font-semibold text-slate-500">No one at Level {lvl.level} yet</p>
                            <p className="text-xs">Grow your Level {Math.max(1, lvl.level - 1)} network to fill this level.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="pt-3 space-y-2">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">
                            {lvl.members.length} member{lvl.members.length !== 1 ? 's' : ''} at this level
                          </p>
                          {lvl.members.map((member, mi) => {
                            const memberRank = getRankDisplay(member);
                            const memberRankIcon = memberRank.show
                              ? rankTheme(memberRank.themeKey, {}, RANK_ICONS).icon
                              : null;
                            return (
                            <div key={mi} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-200 shadow-sm">
                              <div className={`w-8 h-8 rounded-lg ${lvl.color} flex items-center justify-center text-white text-xs font-black flex-shrink-0`}>
                                {member.username[0].toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-900">{member.username}</p>
                                <p className="text-xs text-slate-400">
                                  Joined {member.joined}
                                  {memberRank.show && (
                                    <>
                                      {' · '}
                                      {memberRankIcon} {memberRank.label}
                                    </>
                                  )}
                                </p>
                              </div>
                              {member.is_active ? (
                                <div className="text-right flex-shrink-0">
                                  <span className="text-xs font-bold bg-emerald-50 border border-emerald-200 text-emerald-600 px-2.5 py-1 rounded-full">✓ Active</span>
                                  <p className="text-[10px] font-black mt-1 text-violet-700">M1: +{formatPts(lvl.month1Pts)}</p>
                                  <p className="text-[10px] font-black text-cyan-700">M2–9: +{formatPts(lvl.month2_9Pts)}/mo</p>
                                </div>
                              ) : (
                                <span className="text-xs font-semibold bg-slate-100 border border-slate-200 text-slate-400 px-2.5 py-1 rounded-full flex-shrink-0">
                                  ⏳ Inactive
                                </span>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Earning pyramids */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-stretch">
          {PAYOUT_PLANS.map((plan, planIndex) => {
            const planTotal = payoutTotals[planIndex];
            const maxPts = Math.max(...plan.pts);
            return (
              <div
                key={plan.id}
                className={`flex h-full flex-col overflow-hidden rounded-2xl border border-white/90 bg-white/95 shadow-[0_16px_44px_-20px_rgba(15,23,42,0.2)] ${plan.shadow}`}
              >
                <div className={`flex min-h-[168px] flex-col justify-between bg-gradient-to-r ${plan.headerGradient} px-5 py-5 text-white`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">Income Payout</p>
                      <p className="mt-1 text-base font-black sm:text-lg">{plan.title}</p>
                      <p className="mt-1 min-h-[2.5rem] text-xs leading-snug text-white/75">{plan.subtitle}</p>
                    </div>
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 text-xl backdrop-blur-sm">
                      {plan.icon}
                    </div>
                  </div>
                  <div className="mt-4 inline-flex w-fit items-baseline gap-1.5 rounded-2xl bg-white/15 px-4 py-2 backdrop-blur-sm">
                    <span className="text-2xl font-black">{formatPts(planTotal)}</span>
                    <span className="text-sm font-semibold text-white/80">
                      pts {plan.id === 'month2_9' ? '/ month' : 'total'}
                    </span>
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <div className="space-y-2.5">
                    {LEVEL_CONFIG.map((cfg, i) => {
                      const apiLvl = data.levels?.find(l => l.level === cfg.level);
                      const count = apiLvl?.count || 0;
                      const pts = plan.pts[i];
                      return (
                        <div key={`${plan.id}-${cfg.level}`} className="group">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className={`flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br ${cfg.barGradient} text-[10px] font-black text-white`}>
                                {cfg.level}
                              </span>
                              <span className="text-xs font-semibold text-slate-700">{cfg.label}</span>
                            </div>
                            <span className={`text-xs font-black ${plan.accent}`}>
                              +{formatPts(pts)}{plan.id === 'month2_9' ? '/mo' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="h-9 flex-1 overflow-hidden rounded-xl bg-slate-100">
                              <div
                                className={`flex h-full items-center justify-between rounded-xl bg-gradient-to-r ${cfg.barGradient} px-3 shadow-sm transition-all duration-500 group-hover:brightness-105`}
                                style={{ width: barWidth(pts, maxPts) }}
                              >
                                <span className="truncate text-[10px] font-bold text-white/95">{cfg.label}</span>
                              </div>
                            </div>
                            <span className="w-14 flex-shrink-0 text-right text-[10px] font-medium text-slate-400">
                              {count > 0 ? `${count} ref${count !== 1 ? 's' : ''}` : '—'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className={`mt-auto rounded-xl border ${plan.accentBorder} ${plan.accentBg} px-4 py-3 text-center`}>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Full chain potential</p>
                    <p className={`mt-1 text-sm font-black ${plan.accent}`}>
                      {formatPts(planTotal)} pts = ₹{formatPts(planTotal)}
                      {plan.id === 'month2_9' ? ' / month' : ''}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

export default Network;